import { STRAPI_URL, flattenStrapiEntry, parseErrorMessage, strapiAuthHeaders } from './strapi';

export type StrapiUserTypeRow = {
  numericId: number | null;
  documentId: string | null;
  type_code: string;
  type_name: string;
  description: string;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function normalizeRow(raw: Record<string, unknown>): StrapiUserTypeRow {
  const s = flattenStrapiEntry(raw);
  const rid = raw.id ?? s.id;
  const d = raw.documentId ?? s.documentId;
  return {
    numericId: rid != null && Number.isFinite(Number(rid)) ? Number(rid) : null,
    documentId: d != null ? String(d) : null,
    type_code: String(s.type_code ?? ''),
    type_name: String(s.type_name ?? ''),
    description: String(s.description ?? ''),
  };
}

/** Canonical user types for superadmin pickers (`type_code` is stable across environments). */
export async function strapiListUserTypes(jwt: string): Promise<StrapiUserTypeRow[]> {
  const params = new URLSearchParams({
    'pagination[pageSize]': '200',
    'pagination[page]': '1',
    sort: 'type_code:asc',
  });
  const res = await fetch(`${STRAPI_URL}/api/user-types?${params}`, {
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) {
    throw new Error(parseErrorMessage(body, res.status));
  }
  const b = asRecord(body);
  const rows = Array.isArray(b?.data) ? (b!.data as Record<string, unknown>[]) : [];
  return rows.map((r) => normalizeRow(r));
}

export type StrapiUserTypeInput = {
  type_code: string;
  type_name: string;
  description?: string;
};

export async function strapiCreateUserType(jwt: string, input: StrapiUserTypeInput): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/user-types`, {
    method: 'POST',
    headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      data: {
        type_code: input.type_code.trim(),
        type_name: input.type_name.trim(),
        description: (input.description ?? '').trim(),
      },
    }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiUpdateUserType(
  jwt: string,
  documentId: string,
  input: Partial<StrapiUserTypeInput>,
): Promise<void> {
  const data: Record<string, unknown> = {};
  if (input.type_code !== undefined) data.type_code = input.type_code.trim();
  if (input.type_name !== undefined) data.type_name = input.type_name.trim();
  if (input.description !== undefined) data.description = (input.description ?? '').trim();
  if (Object.keys(data).length === 0) return;

  const res = await fetch(`${STRAPI_URL}/api/user-types/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    headers: { ...strapiAuthHeaders(jwt), 'Content-Type': 'application/json' },
    body: JSON.stringify({ data }),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}

export async function strapiDeleteUserType(jwt: string, documentId: string): Promise<void> {
  const res = await fetch(`${STRAPI_URL}/api/user-types/${encodeURIComponent(documentId)}`, {
    method: 'DELETE',
    headers: strapiAuthHeaders(jwt),
  });
  const body = (await res.json().catch(() => ({}))) as unknown;
  if (!res.ok) throw new Error(parseErrorMessage(body, res.status));
}
